import React, { useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import Navbar from "./Navbar";
import Footer from "./Footer";


export default function PageLayout({ children }) {
  const location = useLocation();
  const isHome = location.pathname === "/";
  const navWrapRef = useRef(null);

  // Keep --navH up to date
  useEffect(() => {
    const apply = () => {
      const h = navWrapRef.current?.offsetHeight ?? 0;
      document.documentElement.style.setProperty("--navH", `${h}px`);
    };
    apply();

    const ro = new ResizeObserver(apply);
    if (navWrapRef.current) ro.observe(navWrapRef.current);
    window.addEventListener("resize", apply);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", apply);
    };
  }, []);

  return (
    <div className="min-h-screen relative font-[Cormorant_Garamond] text-center">
      {/* === Fixed background (simplified and optimized) === */}
      <div className="fixed-background">
        {/* Solid color fallback */}
        <div className="bg-fallback" />
        
        {/* Animated gradient base */}
        <div className="animated-gradient" />
        
        {/* Base parchment */}
        <div className="parchment-bg" />
        
        {/* Blurred clone for larger screens */}
        <div className="blurred-bg" />
        
        {/* Light glow/wash */}
        <div className="light-glow" />
      </div>
      {/* === /Fixed background === */}

      {/* Content */}
      <div className="relative z-10">
        <div id="site-nav" ref={navWrapRef}>
          <Navbar />
        </div>

        {/* Logo */}
        <div className="w-full flex justify-center pt-4 pb-4">
          <Link to="/">
            <img
              src="/icons/logo.webp"
              alt="Whispers of the Mystic Tarot Cards"
              className="w-[160px] lg:w-[200px] h-auto object-contain animate-mysticReveal"
              onContextMenu={(e) => e.preventDefault()}
            />
          </Link>
        </div>

        {/* Page content card */}
        <div
          className={
            isHome
              ? "max-w-[1150px] mx-auto px-4"
              : "w-full max-w-[95%] sm:max-w-2xl lg:max-w-4xl xl:max-w-[1000px] mx-auto bg-white/15 backdrop-blur-sm border border-[#e6dff5]/50 rounded-lg p-4 sm:p-6 text-left text-[#3f2d61] shadow-md"
          }
        >
          {children}
        </div>

        {/* Ads */}
     
        <br />
      </div>

      {/* Footer (independent from background) */}
      <div className="relative z-8">
        <Footer />
      </div>

      <style jsx>{`
        /* Fixed background container - UPDATED */
        .fixed-background {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          min-height: 100vh; /* Changed from height to min-height */
          z-index: -60;
          pointer-events: none;
          overflow: hidden;
        }
        
        /* All background layers */
        .fixed-background > div {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
        }
        
        /* Solid color fallback */
        .bg-fallback {
          background: #0f3d3f;
        }
        
        /* Animated gradient */
        .animated-gradient {
          background: linear-gradient(135deg, #e9fffb, #c9fbf3, #f4fffd);
          background-size: 400% 400%;
          animation: gradientAnimation 15s ease infinite;
        }
        
        /* Parchment background */
        .parchment-bg {
          background-image: url('/icons/background1.webp');
          background-repeat: no-repeat;
          background-size: cover;
          background-position: bottom;
          filter: brightness(1.06) saturate(112%);
        }
        
        /* Blurred background for larger screens */
        .blurred-bg {
          display: none;
          background-image: url('/icons/background1.webp');
          background-repeat: no-repeat;
          background-size: 102% 102%;
          background-position: center;
          filter: brightness(1.05) saturate(112%) blur(2.5px);
        }
        
        /* Light glow effect */
        .light-glow {
          background: 
            radial-gradient(70% 55% at 50% 12%, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0) 10%, rgba(255,255,255,.04) 70%),
            radial-gradient(120% 20% at 45% 10%, rgba(0,70,80,.04) 10%, rgba(0,80,70,.02) 35%, rgba(0,60,60,.08) 80%),
            linear-gradient(135deg, rgba(0,100,90,.12) 10%, rgba(0, 110, 101, 0.08) 42%, rgba(0,70,70,.08) 80%);
        }
        
        /* Gradient animation */
        @keyframes gradientAnimation {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        /* Media queries for responsive behavior */
        @media (min-width: 768px) {
          .blurred-bg {
            display: block;
          }
        }
        
        /* Prevent white space on mobile - UPDATED */
        @media (max-width: 768px) {
          .fixed-background {
            min-height: 100vh; /* Changed from height to min-height */
            height: -webkit-fill-available; /* For iOS Safari */
          }
          
          /* Prevent background movement on scroll */
          .parchment-bg {
            background-attachment: initial;
          }
        }
        
        /* Fix for iOS Safari to prevent white gap - UPDATED */
        @supports (-webkit-touch-callout: none) {
          .fixed-background {
            min-height: -webkit-fill-available; /* Changed from height to min-height */
          }
        }
      `}</style>
    </div>
  );
}